import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { postApi } from 'redux/apis';
import styles from './index.module.scss';
import { toastText } from 'utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { fetchProfileAction } from 'redux/action/profileAction';
import {
	getCompanies,
	getCompanyDetails,
	updateCompanyConnect,
	updateCompanyStatus,
} from 'redux/slice/companySlice';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import { Modal } from 'antd';
import jwtDecode from 'jwt-decode';
import { showNotification } from 'utils/notification';

const QuickbooksCallback = () => {
	const [isLoading, setIsLoading] = useState(false);

	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();

	const [open, setOpen] = useState(false);

	const { data: companiesData } = useSelector(
		(state: any) => state?.companies
	);

	useEffect(() => {
		const run = async () => {
			setIsLoading(true);

			if (
				localStorage.getItem('isLogin') &&
				localStorage.getItem('isLogin') === 'true'
			) {
				try {
					// Try to get email from localStorage first
					let userEmail: string | null = localStorage.getItem('disconnect-userEmail');

					// If not in localStorage, try to extract from current accessToken
					if (!userEmail) {
						const currentAccessToken = localStorage.getItem('accessToken');
						if (currentAccessToken) {
							try {
								const decoded: any = jwtDecode(currentAccessToken);
								userEmail = decoded?.email;
							} catch (err) {
								console.error('Error decoding token:', err);
							}
						}
					}

					// If still no email, redirect to login
					if (!userEmail) {
						toastText('Unable to identify user. Please login again.', 'error');
						localStorage.clear();
						navigate('/login');
						setIsLoading(false);
						return;
					}

					// Get realmId from localStorage
					const realmId = localStorage.getItem('disconnect-companyId');

					// realmId is required for disconnect flow
					if (!realmId) {
						toastText('Invalid disconnect request. Missing company information.', 'error');
						localStorage.removeItem('isLogin');
						localStorage.removeItem('disconnect-companyId');
						localStorage.removeItem('disconnect-userEmail');
						navigate('/');
						setIsLoading(false);
						return;
					}

					const machineId = uuidv4();
					postApi('/quickbooks/sso-callback', {
						url: window.location.href,
						isLogin: localStorage.getItem('isLogin') === 'true' ? true : false,
						machineId: machineId,
						userEmail: userEmail,
						realmId: realmId,
					})
						.then((response) => {
							localStorage.setItem(
								'accessToken',
								response?.data?.data?.accessToken
							);
							localStorage.setItem(
								'refreshToken',
								response?.data?.data?.refreshToken
							);
							localStorage.removeItem('isLogin');
						})
						.then(() => {
							dispatch(fetchProfileAction() as any)
								.unwrap()
								.then((res: any) => {
									dispatch(getCompanies(res));
									const disConnectRealmId = localStorage.getItem(
										'disconnect-companyId'
									);
									if (disConnectRealmId) {
										let disConnectCompanyId: string | null = null;

										const companyData = res.data.companies.find(
											(com: any) => com?.company?.tenantID === disConnectRealmId
										);

										if (companyData && companyData.companyId) {
											disConnectCompanyId = companyData.companyId;
										}

										if (disConnectCompanyId && companyData) {
											disconnectCompany(disConnectCompanyId, companyData)
												.then(() => {
													localStorage.removeItem('disconnect-companyId');
													localStorage.removeItem('disconnect-userEmail');
													localStorage.setItem('settings', 'Integrations');
													setIsLoading(false);
													navigate('/?openDrawer=true');
												})
												.catch((err) => {
													console.error('Disconnect error:', err);
													setIsLoading(false);
													localStorage.removeItem('disconnect-companyId');
													localStorage.removeItem('disconnect-userEmail');
													navigate('/?openDrawer=true');
												});
										} else {
											console.error('No companyId found, cannot disconnect');
											toastText('No companyId found, cannot disconnect', 'error');
											localStorage.removeItem('disconnect-companyId');
											localStorage.removeItem('disconnect-userEmail');
											navigate('/');
											setIsLoading(false);
										}
									} else {
										console.log('No disconnect-companyId in localStorage');
										localStorage.removeItem('disconnect-userEmail');
										navigate('/');
										setIsLoading(false);
									}
								})
								.catch((err: any) => {
									console.error('Profile fetch error:', err);
									setIsLoading(false);
									localStorage.removeItem('disconnect-companyId');
									localStorage.removeItem('disconnect-userEmail');
									navigate('/?openDrawer=true');
								});
						})
						.catch((err) => {
							setIsLoading(false);
							if (
								err.response?.data?.message ===
								'Your QuickBooks email is not verified'
							) {
								setOpen(true);
								return;
							}

							// Check if it's an unauthorized user error
							const errorMessage = err.response?.data?.message || '';
							if (
								errorMessage.includes("You don't have permission to disconnect this company. Please log in with")
							) {
								// Show notification similar to QuickBooks session expired
								showNotification({
									type: 'error',
									message: 'Access Denied',
									description: errorMessage,
									duration: 10,
								});

								// Clean up and redirect to login
								localStorage.clear();

								setTimeout(() => {
									navigate('/login');
								}, 2000);
								return;
							}

							toastText(err.response?.data?.message || 'An error occurred', 'error');
							localStorage.clear();
							navigate('/login');
						});
				} catch (err: any) {
					setIsLoading(false);
					toastText(err.response?.data?.message, 'error');
					navigate('/login');
				}
			} else if (
				localStorage.getItem('isFromAppInstall') &&
				localStorage.getItem('isFromAppInstall') === 'true'
			) {
				try {
					const machineId = uuidv4();
					postApi('/quickbooks/get-app-now/callback', {
						url: window.location.href,
						machineId: machineId,
						companyId: null,
					})
						.then((response) => {
							localStorage.setItem(
								'accessToken',
								response?.data?.data?.accessToken
							);
							localStorage.setItem(
								'refreshToken',
								response?.data?.data?.refreshToken
							);
							localStorage.removeItem('isFromAppInstall');
						})
						.then(() => {
							dispatch(fetchProfileAction() as any)
								.unwrap()
								.then((res: any) => {
									localStorage.removeItem('companyId');
									localStorage.removeItem('payPeriod');
									dispatch(getCompanies(res));
									navigate('/?openDrawer=true&drawerSelection=Integrations');
									setIsLoading(false);
								})
								.then(() => {
									dispatch(
										getCompanyDetails(localStorage.getItem('companyId'))
									);
								});
						})
						.catch((err) => {
							setIsLoading(false);
							if (
								err.response?.data?.message ===
								'Your QuickBooks email is not verified'
							) {
								setOpen(true);
								return;
							}
							toastText(err.response?.data?.message, 'error');
							// localStorage.clear();
							navigate('/');
						});
				} catch (err: any) {
					setIsLoading(false);
					if (
						err.response?.data?.message ===
						'Your QuickBooks email is not verified'
					) {
						setOpen(true);
						return;
					}
					toastText(err.response?.data?.message, 'error');
					navigate('/login');
				}
			} else {
				const isConnectionExisting = localStorage.getItem(
					'isConnectionExisting'
				);

				// Check if it's from new plan (set in BuySubscriptionModal or reconnect flow)
				const isNewOutsetaPlan = localStorage.getItem('isNewOutsetaPlan') === 'true';
				// Check if transferSubscription flag is set (set when reauthorize is true and new company admin clicks reconnect)
				const transferSubscription = localStorage.getItem('transferSubscription') === 'true';
				// Always use the consistent localStorage key for account UID (set in BuySubscriptionModal or reconnect flow)
				const outsetaAccountUidForConnection = localStorage.getItem('outsetaAccountUidForConnection');

				if (!outsetaAccountUidForConnection) {
					console.error("No account UID found for connection");
					setIsLoading(false);
					toastText('Unable to find account information. Please try again.', 'error');
					return;
				}

				const callbackPayload: any = {
					url: window.location.href,
					outSetaToken: localStorage.getItem('outsetaAccessToken'),
					companyId:
						isConnectionExisting && isConnectionExisting === 'true'
							? localStorage.getItem('companyId') || null
							: null,
					outSetaAccountUid: outsetaAccountUidForConnection,
				};

				// Set isNewOutsetaPlan flag if it's a new plan
				if (isNewOutsetaPlan) {
					callbackPayload.isNewOutsetaPlan = true;
				}

				// Set transferSubscription flag if reauthorization is required (role transfer scenario)
				if (transferSubscription) {
					callbackPayload.transferSubscription = true;
				}

				postApi('/quickbooks/callback', callbackPayload)
					.then(() => {
						// Clean up the flags after use
						localStorage.removeItem('isNewOutsetaPlan');
						localStorage.removeItem('outsetaAccountUidForConnection');
						localStorage.removeItem('transferSubscription');
						setIsLoading(false);
						dispatch(fetchProfileAction())
							.unwrap()
							.then((res) => {
								localStorage.removeItem('companyId');
								localStorage.removeItem('payPeriod');
								dispatch(getCompanies(res));
							})
							.then(() => {
								dispatch(getCompanyDetails(localStorage.getItem('companyId')));
							})
							.then(() => {
								localStorage.setItem('settings', 'Configurations');
								navigate('/?openDrawer=true&createNewPayPeriod=true');
							});
						localStorage.removeItem('qbUrl');
					})
					.catch((err: any) => {
						setIsLoading(false);
						// Clean up flags on error
						localStorage.removeItem('transferSubscription');
						if (
							err?.response?.data?.message === 'Can not connect this company'
						) {
							toast.error(
								'You are trying to connect different companies. Please connect the same company that you have previously connected.',
								{
									style: {
										fontSize: '16px',
									},
									duration: 5000,
								}
							);
							setTimeout(() => {
								window.open(localStorage.getItem('qbUrl') as string, '_self');
							}, 5000);
						} else {
							toastText(err?.response?.data?.message, 'error');
							if (
								err.response?.data?.message ===
								'Your QuickBooks email is not verified'
							) {
								setOpen(true);
								return;
							}
							navigate('/');
						}
					});
			}
		};
		run();
	}, []);

	const disconnectCompany = async (companyId: string, companyData?: any) => {
		try {
			// Use provided companyData if available, otherwise find from Redux state
			const company = companyData || companiesData?.find((c: any) => c?.companyId === companyId || c?.company?.id === companyId);

			if (!company) {
				console.error("No company found for disconnect");
				toastText('Company not found. Please try again.', 'error');
				return;
			}

			// Get subscription info from found company (subscription is an array, take first element)
			const subscriptions = company?.company?.Subscription;
			const subscription = subscriptions && Array.isArray(subscriptions) ? subscriptions[0] : null;
			const newPlanUid = process.env.REACT_APP_OUTSETA_PLAN_UID_2;

			// Check if subscription exists and has zohoproductid
			let isNewOutsetaPlan = false;
			let outSetaAccountUid: string | null = null;

			if (subscription && newPlanUid) {
				// Check if zohoproductid matches the new plan UID
				const zohoProductId = subscription?.zohoProductId || subscription?.zohoSubscriptionPlan?.Plan.Uid;

				if (zohoProductId && zohoProductId === newPlanUid) {
					isNewOutsetaPlan = true;
				} else {
					isNewOutsetaPlan = false;
				}

				// Get account UID from subscription (same for both cases)
				outSetaAccountUid = subscription?.zohoSubscriptionPlan?.Account?.Uid || null;

				if (!outSetaAccountUid) {
					console.error("No account UID found");
					return;
				}
			} else {
				console.error("No subscription found");
				return;
			}

			const disconnectPayload: any = {
				companyId: companyId,
				outSetaToken: localStorage.getItem('outsetaAccessToken'),
				isNewOutsetaPlan: isNewOutsetaPlan,
				outSetaAccountUid: outSetaAccountUid,
			};

			const res = await postApi('/quickbooks/disconnect', disconnectPayload);

			dispatch(
				updateCompanyConnect({
					companyId: companyId,
					value: false,
				})
			);

			toastText(res?.data?.message, 'success');

			const params = {
				data: {
					key: companyId,
				},
				value: false,
			};

			dispatch(updateCompanyStatus(params));
		} catch (error: any) {
			console.error('[disconnectCompany] Error:', error);
			toastText(error?.response?.data?.message, 'error');
			throw error; // Re-throw to be caught by caller
		}
	};

	return (
		<div className={styles['main-container']}>
			{isLoading && <img src="assets/gifs/loading-black.gif" />}
			<Modal
				open={open}
				onCancel={() => {
					localStorage.clear();
					navigate('/');
					setOpen(false);
				}}
				footer={false}
			>
				Your email is not yet verified. Please{' '}
				<a href="https://accounts.intuit.com/app/account-manager/security">
					login to your intuit account
				</a>
			</Modal>
		</div>
	);
};

export default QuickbooksCallback;
